import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Package, Search, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  name: '',
  codigo: '',
  categoria: '',
  descricao_resumida: '',
  descricao_detalhada: '',
  descricao_tecnica: '',
  unidade: '',
  linked_variables: [],
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [units, setUnits] = useState([]);
  const [allVariables, setAllVariables] = useState([]);

  const loadProducts = useCallback(async (searchParam = '') => {
    try {
      const [productsRes, unitsRes, varsRes] = await Promise.all([
        axios.get(`${API_URL}/products`, { withCredentials: true, params: searchParam ? { search: searchParam } : {} }),
        axios.get(`${API_URL}/units-of-measure`, { withCredentials: true }),
        axios.get(`${API_URL}/variables`, { withCredentials: true }),
      ]);
      setProducts(productsRes.data);
      setUnits(unitsRes.data);
      setAllVariables(varsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openCreate = () => {
    setEditingProduct(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      codigo: product.codigo || '',
      categoria: product.categoria || '',
      descricao_resumida: product.descricao_resumida || '',
      descricao_detalhada: product.descricao_detalhada || '',
      descricao_tecnica: product.descricao_tecnica || '',
      unidade: product.unidade || '',
      linked_variables: product.linked_variables || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.product_id}`, formData, { withCredentials: true });
        toast.success('Produto atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/products`, formData, { withCredentials: true });
        toast.success('Produto criado com sucesso');
      }
      setDialogOpen(false);
      setEditingProduct(null);
      setFormData({ ...EMPTY_FORM });
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`${API_URL}/products/${productId}`, { withCredentials: true });
      toast.success('Produto excluído com sucesso');
      setDeleteTarget(null);
      loadProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingProduct(null);
      setFormData({ ...EMPTY_FORM });
    }
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">{"Produtos e Servi\u00e7os"}</h1>
          <p className="text-[#6E6E73]">{"Cat\u00e1logo de produtos"}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-product-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-[#6E6E73]" />
          <Input
            placeholder="Buscar por nome ou código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadProducts()}
            className="flex-1"
            data-testid="product-search-input"
          />
          <Button onClick={loadProducts} className="bg-[#AF2628] hover:bg-[#961B22]">Buscar</Button>
        </div>

        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <div key={product.product_id} className="border border-[#E5E5EA] rounded-lg p-4 hover:shadow-md hover:border-[#AF2628] transition-all" data-testid={`product-card-${product.product_id}`}>
                <div className="flex items-start gap-3">
                  <div className="bg-[#F0F9FF] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#111111] truncate">{product.name}</h3>
                    <p className="text-xs text-[#AEAEB2]">{"C\u00f3digo: "}{product.codigo}</p>
                    {product.descricao_resumida && (
                      <p className="text-sm text-[#6E6E73] mt-1 line-clamp-2">{product.descricao_resumida}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {product.categoria && <span className="text-xs bg-[#F7F7F8] px-2 py-1 rounded">{product.categoria}</span>}
                      <span className="text-xs bg-[#F0FDF4] px-2 py-1 rounded text-[#22C55E]">{product.unidade}</span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(product)} className="text-xs" data-testid={`edit-product-${product.product_id}`}>
                        <Pencil className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleteTarget(product)} className="text-xs text-red-600 hover:text-red-700" data-testid={`delete-product-${product.product_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73]">Nenhum produto cadastrado</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="product-name-input" />
              </div>
              <div>
                <Label>{"C\u00f3digo"}</Label>
                <Input value={formData.codigo} onChange={(e) => setFormData({ ...formData, codigo: e.target.value })} placeholder="Auto-gerado se vazio" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={formData.categoria} onChange={(e) => setFormData({ ...formData, categoria: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>{"Descri\u00e7\u00e3o Resumida"}</Label>
                <Input value={formData.descricao_resumida} onChange={(e) => setFormData({ ...formData, descricao_resumida: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>{"Descri\u00e7\u00e3o Detalhada"}</Label>
                <Textarea value={formData.descricao_detalhada} onChange={(e) => setFormData({ ...formData, descricao_detalhada: e.target.value })} rows={3} />
              </div>
              <div>
                <Label>Unidade de Medida</Label>
                <Select value={formData.unidade} onValueChange={(val) => setFormData({ ...formData, unidade: val })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => <SelectItem key={u.unit_id} value={u.name}>{u.name} ({u.abbreviation})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Linked Variables */}
            <div className="border border-[#E5E5EA] rounded-lg p-4 bg-[#FAFAFA]">
              <h4 className="text-sm font-semibold text-[#111111] mb-2">Variaveis Vinculadas (Premissas)</h4>
              <p className="text-[10px] text-[#AEAEB2] mb-3">Ao vincular variaveis, o usuario sera solicitado a preenchê-las no diagnostico do documento</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {allVariables.map(v => {
                  const linked = formData.linked_variables.find(lv => lv.var_name === v.name);
                  return (
                    <div key={v.var_id} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!linked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, linked_variables: [...formData.linked_variables, { var_name: v.name, required: false }] });
                          } else {
                            setFormData({ ...formData, linked_variables: formData.linked_variables.filter(lv => lv.var_name !== v.name) });
                          }
                        }}
                        className="w-4 h-4 accent-[#AF2628]"
                        data-testid={`link-var-${v.name}`}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-mono font-bold text-[#AF2628]">{v.name}</span>
                        <span className="text-xs text-[#6E6E73] ml-2">{v.label}</span>
                      </div>
                      {linked && (
                        <label className="flex items-center gap-1 text-[10px] text-[#6E6E73] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={linked.required}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                linked_variables: formData.linked_variables.map(lv =>
                                  lv.var_name === v.name ? { ...lv, required: e.target.checked } : lv
                                )
                              });
                            }}
                            className="w-3 h-3 accent-[#AF2628]"
                          />
                          Obrigatoria
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="product-submit-button">
                {submitting ? 'Salvando...' : (editingProduct ? 'Salvar' : 'Criar Produto')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {"Confirmar Exclus\u00e3o"}
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o produto <strong>{deleteTarget?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.product_id)} data-testid="confirm-delete-product">
              Excluir Produto
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
